import { useEffect, useRef, useState } from 'react'
import {
  type DateRange as DateRangePickerType,
  type DayButtonProps,
  DayPicker,
  type Matcher,
  rangeContainsModifiers,
} from 'react-day-picker'
import { enUS, is } from 'react-day-picker/locale'
import { useIntl } from 'react-intl'
import { addDays, isSameDay, subDays } from 'date-fns'
import { format } from 'date-fns-tz'
import styled from 'styled-components'

import { Box } from 'ui-primitives/src/Box'
import { useBreakpoints } from 'ui-primitives/src/hooks/useBreakpoints'
import { spacing } from 'ui-primitives/src/utils/spacing'
import { NavArrow } from 'bl-common/src/elements/Arrow/Arrow'
import { Disclaimer } from 'flow-builder/src/components/Disclaimer'
import { Text } from 'flow-builder/src/components/Text/Text'

import { DayPickerStyles } from './DayPickerStyles'
import { bookingBarMessages } from './messages'
import type { PopoverState } from './Popover'
import {
  Popover,
  PopoverContent,
  type PopoverContentProps,
  PopoverTrigger,
} from './Popover'

type Target = 'from' | 'to'

// make fields required
export type DateRange = DateRangePickerType & {
  from: NonNullable<DateRangePickerType['from']>
  to: DateRangePickerType['to']
}

type Props = {
  onChange: (range: DateRange) => void
  popover: PopoverContentProps & PopoverState
  checkoutDays?: Date[]
  disabledDays?: Matcher[]
  maxDate?: Date
  minDays?: number
  value?: DateRange
}

/**
 * DateRangePicker component
 *
 * The `DateRangePicker` component allows users to select a date range (check-in and check-out dates).
 *
 * @param props - The properties for the `DateRangePicker` component.
 * @param props.onChange - A callback function triggered when the date range changes.
 * @param props.popover - Configuration for the popover.
 * @param props.popover.open - A boolean indicating whether the popover is open.
 * @param props.popover.setOpen - A function to toggle the popover's open state.
 * @param props.popover.onBack - A callback function triggered when the back button is clicked.
 * @param props.popover.onNext - A callback function triggered when the next button is clicked.
 * @param props.popover.anchor - HTML element used as the anchor for the popover.
 * @param props.checkoutDays - An array of dates that are marked as checkout-only days.
 * @param props.disabledDays - An array of matchers for days that are disabled and cannot be selected.
 * @param props.maxDate - The maximum selectable date in the calendar.
 * @param props.minDays - The minimum number of days required between the `from` and `to` dates. Defaults to `1`.
 * @param props.value - The currently selected date range.
 *
 * @returns The rendered `DateRangePicker` component.
 */
export const DateRangePicker = (props: Props) => {
  const {
    checkoutDays = [],
    disabledDays = [],
    onChange,
    value,
    maxDate,
    minDays = 1,
    popover: { open, setOpen, onBack, onNext, anchor },
  } = props
  const { formatMessage, locale } = useIntl()
  const { isMobile } = useBreakpoints()
  const [target, setTarget] = useState<Target>('from')

  const anchorRef = useRef<HTMLDivElement | null>(null)
  if (anchor) {
    anchorRef.current = anchor
  }

  const DayButton = (props: DayButtonProps) => {
    const {
      day: { date: selectedDay },
      modifiers,
      onClick: _onClick, // override default in favor of our custom
      ...buttonProps
    } = props
    const ref = useRef<HTMLButtonElement>(null)

    const onDaySelect = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()

      const from = target === 'from' ? selectedDay : value?.from
      const to = target === 'to' ? selectedDay : value?.to

      const hasMinLength = from && to && addDays(from, minDays) <= to
      const isDoubleClick = event.detail === 2

      const shouldResetSelection =
        isDoubleClick ||
        !hasMinLength ||
        (to &&
          rangeContainsModifiers({ from, to }, checkoutDays) &&
          !isValidCheckout(from, to, checkoutDays, disabledDays)) ||
        rangeContainsModifiers({ from, to }, disabledDays)

      const shouldPreventSelection =
        (from && daysIncludesDate(checkoutDays, from)) ||
        (to &&
          daysIncludesDate(checkoutDays, to) &&
          (isDoubleClick ||
            !hasMinLength ||
            !isValidCheckout(from, to, checkoutDays, disabledDays)))

      if (shouldPreventSelection) {
        return
      }

      if (target === 'from') {
        setTarget('to')
      }

      onChange(
        shouldResetSelection
          ? { from: selectedDay, to: undefined }
          : { from, to }
      )
    }

    useEffect(() => {
      if (modifiers.focused) {
        ref.current?.focus()
      }
    }, [modifiers.focused])

    return (
      <button
        // biome-ignore lint/a11y/noAutofocus: scroll selected day into view for mobile
        autoFocus={isMobile ? modifiers.selected : false}
        onClick={onDaySelect}
        ref={ref}
        {...buttonProps}
      />
    )
  }

  const message = (
    <Disclaimer severity="info">
      {formatMessage(
        target === 'from'
          ? bookingBarMessages.label.selectArrivalDate
          : bookingBarMessages.label.selectDepartureDate
      )}
    </Disclaimer>
  )

  return (
    <Popover
      modal={isMobile}
      open={open}
      onOpenChange={(open: boolean) => {
        setOpen(open)
      }}
    >
      <Box flexDirection="row" gap="1px" background="var(--border-color)">
        <PopoverTrigger
          title={formatMessage(bookingBarMessages.menu.checkIn)}
          value={
            value?.from
              ? format(value?.from, 'MM/dd/yyyy')
              : formatMessage(bookingBarMessages.label.pleaseSelect)
          }
          onClick={() => {
            setTarget('from')

            if (!open) {
              setOpen(true)
            }
          }}
          selected={open && target === 'from'}
        />
        <PopoverTrigger
          title={formatMessage(bookingBarMessages.menu.checkOut)}
          value={
            value?.to
              ? format(value.to, 'MM/dd/yyyy')
              : formatMessage(bookingBarMessages.label.pleaseSelect)
          }
          onClick={() => {
            setTarget('to')

            if (!open) {
              setOpen(true)
            }
          }}
          selected={open && target === 'to'}
        />
      </Box>
      <PopoverContent
        anchor={anchorRef.current}
        mobileFooterContent={message}
        onBack={onBack}
        {...(value?.to && { onNext })} // only allow next when range is complete
      >
        <Box
          flexDirection="column"
          justifyContent="center"
          padding={`${spacing[1]} ${spacing[2]}`}
          boxShadow="var(--box-shadow)"
        >
          {isMobile && (
            <Box padding={spacing[1]} paddingBottom={spacing[2]}>
              <Box>
                <Text
                  type="headingMedium"
                  value={formatMessage(bookingBarMessages.title.whatDate)}
                />
              </Box>
              <Box paddingTop={spacing[2]}>
                <Text
                  type="subheading"
                  value={formatMessage(bookingBarMessages.title.openEveryDay)}
                />
              </Box>
              <Box paddingTop={spacing[2]}>
                <CalendarButtonInfo />
              </Box>
            </Box>
          )}
          <DayPicker
            mode="range"
            locale={locale === 'is' ? is : enUS}
            selected={value}
            onSelect={() => void 0} // keeping the component controlled, event is handled by our custom DayButton
            components={{
              DayButton,
              Nav: ({ onPreviousClick, onNextClick }) => {
                return !isMobile ? (
                  <Box position="absolute" flexDirection="row" $height="44px">
                    <Box
                      flex="1"
                      alignItems="flex-start"
                      justifyContent="center"
                    >
                      <button
                        type="button"
                        onClick={event => {
                          onPreviousClick?.(event)
                        }}
                        style={{
                          cursor: 'pointer',
                          padding: spacing[0.5],
                          margin: spacing[-0.5],
                          outline: 'none',
                        }}
                      >
                        <NavArrow
                          rotate={0}
                          scale={1}
                          color="var(--color-link)"
                        />
                      </button>
                    </Box>
                    <Box flex="1" alignItems="flex-end" justifyContent="center">
                      <button
                        type="button"
                        onClick={event => {
                          onNextClick?.(event)
                        }}
                        style={{
                          cursor: 'pointer',
                          padding: spacing[0.5],
                          margin: spacing[-0.5],
                          outline: 'none',
                        }}
                      >
                        <NavArrow
                          rotate={180}
                          scale={1}
                          color="var(--color-link)"
                        />
                      </button>
                    </Box>
                  </Box>
                ) : (
                  <></>
                )
              },
            }}
            {...(!isMobile && {
              footer: <Footer message={message} />,
            })}
            modifiers={{
              checkout: checkoutDays,
            }}
            modifiersClassNames={{
              checkout: 'rdp-checkout_day',
            }}
            disabled={disabledDays}
            excludeDisabled // prevents selection which includes a disabled day
            defaultMonth={isMobile ? new Date() : value?.from}
            numberOfMonths={isMobile ? 12 : 2}
            endMonth={maxDate}
            styles={{
              month_caption: {
                justifyContent: isMobile ? 'flex-start' : 'center',
                marginLeft: isMobile ? spacing[0.5] : 0,
              },
              months: isMobile
                ? {
                    justifySelf: 'center',
                    justifyContent: 'center',
                  }
                : {
                    justifyContent: 'space-between',
                    width: '100%',
                    maxWidth: 'none',
                  },
              day: {
                padding: '0',
              },
            }}
          />
        </Box>
      </PopoverContent>
      <DayPickerStyles />
    </Popover>
  )
}

const CalendarButtonInfo = () => {
  const { isMobile } = useBreakpoints()
  const { formatMessage } = useIntl()

  return (
    <Box
      flexDirection="row"
      gap={spacing[1]}
      justifyContent={isMobile ? 'flex-start' : 'flex-end'}
    >
      <Box
        flexDirection="row"
        $width="auto"
        marginRight={spacing[1]}
        alignItems="center"
      >
        <SmallCalendarButton>&nbsp;</SmallCalendarButton>
        <Text
          type="text"
          value={formatMessage(bookingBarMessages.label.checkoutOnly)}
        />
      </Box>
      <Box flexDirection="row" $width="auto" alignItems="center">
        <SmallCalendarButton>-</SmallCalendarButton>
        <Text
          type="text"
          value={formatMessage(bookingBarMessages.label.notAvailable)}
        />
      </Box>
    </Box>
  )
}

const Footer = ({ message }: { message: JSX.Element }) => {
  return (
    <Box flexDirection="row" margin={`${spacing[1]} 0`}>
      <Box>{message}</Box>
      <CalendarButtonInfo />
    </Box>
  )
}

const SmallCalendarButton = styled.button`
  width: 20px;
  height: 20px;
  background-color: var(--rdp-disabled-day-background-color);
  margin-right: ${spacing[1]};
  color: var(--color-text);
`

const daysIncludesDate = (dates: Date[], date: Date) =>
  !!dates.find(d => isSameDay(d, date))

const hasSelectedCheckoutOnly = (from: Date, to: Date, checkoutDays: Date[]) =>
  rangeContainsModifiers({ from, to }, checkoutDays)

const isCheckoutAllowed = (from: Date, to: Date, checkoutDays: Date[]) =>
  daysIncludesDate(checkoutDays, to) &&
  !rangeContainsModifiers({ from, to: subDays(to, 1) }, checkoutDays)

const isValidCheckout = (
  from: Date,
  to: Date,
  checkoutDays: Date[],
  disabledDays: Matcher[]
) =>
  !hasSelectedCheckoutOnly(from, to, checkoutDays) ||
  (hasSelectedCheckoutOnly(from, to, checkoutDays) &&
    isCheckoutAllowed(from, to, checkoutDays) &&
    !rangeContainsModifiers({ from, to }, disabledDays))
